// expiry_v1: ตรวจสอบ subscription + แสดง popup/block screen
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock, X, Phone } from 'lucide-react';
import API from '../api';

const CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000; // 4 ชั่วโมง
const STORAGE_KEY = 'subscription_dismissed_at';

export default function SubscriptionWatch() {
  const [sub, setSub] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await API.get('/api/me/subscription/me');
      setSub(res.data);
      // แสดง warning ถ้า: เป็น warning + ยังไม่ได้ dismiss ใน 4 ชม. ที่ผ่านมา
      if (res.data.is_warning) {
        const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        const elapsed = Date.now() - dismissedAt;
        if (elapsed > CHECK_INTERVAL_MS) {
          setShowWarning(true);
        }
      }
    } catch (err) {
      // ignore — ถ้า API fail ไม่ block user
      console.warn('[SubscriptionWatch] fetch failed:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchStatus(); // run immediately
    const interval = setInterval(fetchStatus, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const dismissWarning = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShowWarning(false);
  };

  if (!sub) return null;

  // ===== Block Screen เมื่อหมดอายุ =====
  if (sub.is_expired) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.96)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          maxWidth: 480, width: '100%',
          background: '#fff', borderRadius: 18,
          padding: '40px 32px', textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}>
          <div style={{
            fontSize: 64, marginBottom: 16,
          }}>🚫</div>
          <h1 style={{
            margin: '0 0 12px', fontSize: 22, fontWeight: 700,
            color: '#dc2626',
          }}>ระบบถูกระงับการใช้งาน</h1>
          <p style={{
            margin: '0 0 24px', fontSize: 14, color: '#475569',
            lineHeight: 1.6,
          }}>
            แพ็กเกจของคุณ{sub.has_license ? `หมดอายุไปแล้ว ${Math.abs(sub.days_left)} วัน` : 'ยังไม่ได้เปิดใช้งาน'}<br/>
            <strong style={{ color: '#dc2626' }}>ลูกค้าที่ทักเข้ามาตอนนี้จะไม่ได้รับการตอบกลับ</strong>
          </p>
          <div style={{
            background: '#fef3c7', borderRadius: 10,
            padding: '14px 16px', marginBottom: 20,
            fontSize: 13, color: '#92400e', textAlign: 'left',
          }}>
            <Phone size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            <strong>กรุณาติดต่อผู้ดูแลระบบเพื่อต่ออายุ</strong>
          </div>
          {/* expiry_buttons_v1: ต่ออายุ + ติดต่อ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            <a href="/renew"
              style={{ padding: "13px 24px", borderRadius: 10, background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff", textDecoration: "none", fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)" }}
            >🔄 ต่ออายุแพ็กเกจทันที</a>
            <a href="https://t.me/mawin1991" target="_blank" rel="noreferrer"
              style={{ padding: "11px 24px", borderRadius: 10, background: "#0ea5e9", color: "#fff", textDecoration: "none", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >💬 ติดต่อผู้ให้บริการ (Telegram)</a>
          </div>
          <button
            onClick={() => { localStorage.clear(); window.location.href = "/login"; }}
            style={{ padding: "10px 24px", borderRadius: 10, background: "transparent", color: "#64748b", border: "1px solid #cbd5e1", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
          >ออกจากระบบ</button>
        </div>
      </div>
    );
  }

  // ===== Warning Popup =====
  if (showWarning && sub.is_warning) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          maxWidth: 420, width: '100%',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b, #f97316)',
            padding: '20px 24px', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <AlertTriangle size={28} />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
              แพ็กเกจใกล้หมดอายุ
            </h2>
          </div>
          <div style={{ padding: '24px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 16px', background: '#fef3c7',
              borderRadius: 10, marginBottom: 16,
            }}>
              <Clock size={20} color="#d97706" />
              <div>
                <div style={{ fontSize: 12, color: '#92400e', marginBottom: 2 }}>คงเหลือ</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#d97706' }}>
                  {sub.days_left} วัน
                </div>
              </div>
            </div>

            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>
              วันหมดอายุ: <strong style={{ color: '#1e293b' }}>
                {new Date(sub.expires_at).toLocaleDateString('th-TH', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
              </strong>
            </p>

            <p style={{
              margin: '0 0 20px', fontSize: 13, color: '#dc2626',
              padding: '10px 12px', background: '#fee2e2', borderRadius: 8,
            }}>
              ⚠️ หากไม่ต่ออายุ ระบบจะถูกระงับอัตโนมัติ<br/>
              ลูกค้าที่ทักเข้ามาจะไม่ได้รับการตอบ
            </p>

            {/* expiry_buttons_v1: ต่ออายุ + ติดต่อ + ปิด */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <a href="/renew"
                style={{ width: "100%", padding: "12px", borderRadius: 10, background: "linear-gradient(135deg, #f59e0b, #f97316)", color: "#fff", textDecoration: "none", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxSizing: "border-box" }}
              >🔄 ต่ออายุแพ็กเกจ</a>
              <a href="https://t.me/mawin1991" target="_blank" rel="noreferrer"
                style={{ width: "100%", padding: "11px", borderRadius: 10, background: "#fff", color: "#0ea5e9", border: "1.5px solid #0ea5e9", textDecoration: "none", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxSizing: "border-box" }}
              >💬 ติดต่อผู้ให้บริการ</a>
              <button onClick={dismissWarning}
                style={{ width: "100%", padding: "10px", borderRadius: 10, background: "transparent", color: "#64748b", border: "1px solid #e2e8f0", fontSize: 13, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              ><X size={14} />ปิดข้อความ (แสดงอีกใน 4 ชม.)</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
